import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchPdfData } from "../services/api";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faShareNodes,
  faFileInvoice,
  faSpinner,
  faTriangleExclamation,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .llc-root {
    --navy:        #0f1f5c;
    --navy-hover:  #0a1540;
    --navy-bg:     #eef1fb;
    --green:       #00b86b;
    --green-hover: #009a59;
    --red:         #dc2626;
    --red-bg:      #fef2f2;
    --border:      #e2e6f3;
    --surface:     #f4f6fc;
    --white:       #ffffff;
    --text:        #111827;
    --muted:       #6b7280;
    --radius:      10px;
    --radius-sm:   7px;
    --font:        'DM Sans', system-ui, sans-serif;
    --font-mono:   'DM Mono', monospace;

    font-family: var(--font);
    /* Use dvh so mobile browser chrome doesn't eat into layout */
    height: 100dvh;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    overflow: hidden;
  }

  /* ── Header ── */
  .llc-header {
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    background: var(--navy);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
    gap: 10px;
  }

  /* Left side: back icon + stacked brand/subtitle */
  .llc-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
  }

  .llc-back-btn {
    width: 34px;
    height: 34px;
    border-radius: var(--radius-sm);
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    color: rgba(255,255,255,0.85);
    transition: background 0.15s;
  }
  .llc-back-btn:hover { background: rgba(255,255,255,0.18); }

  .llc-header-titles {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .llc-header-brand {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    line-height: 1.25;
  }
  .llc-header-sub {
    font-size: 10px;
    color: rgba(255,255,255,0.50);
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: var(--font-mono);
  }

  /* Right side actions */
  .llc-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  /* Icon-only square button — share/overflow */
  .llc-icon-btn {
    width: 34px;
    height: 34px;
    border-radius: var(--radius-sm);
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(255,255,255,0.85);
    transition: background 0.15s;
  }
  .llc-icon-btn:hover { background: rgba(255,255,255,0.18); }
  .llc-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Primary download button — label only on desktop */
  .llc-dl-btn {
    height: 34px;
    padding: 0 14px;
    border-radius: var(--radius-sm);
    background: var(--green);
    border: none;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: var(--font);
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
  }
  .llc-dl-btn:hover:not(:disabled) { background: var(--green-hover); }
  .llc-dl-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  /* On mobile hide the text label, show icon only */
  .llc-dl-btn .llc-dl-label { display: inline; }

  @media (max-width: 480px) {
    .llc-dl-btn {
      width: 34px;
      padding: 0;
      justify-content: center;
    }
    .llc-dl-btn .llc-dl-label { display: none; }
  }

  /* ── Main body ── */
  .llc-body {
    flex: 1;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
  }

  /* ── Viewer card ── */
  .llc-viewer-card {
    flex: 1;
    overflow: hidden;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .llc-viewer-inner {
    flex: 1;
    overflow: auto;
    height: 100%;
    /* smooth momentum scroll on iOS */
    -webkit-overflow-scrolling: touch;
  }

  /* ── State screens (loading / error) ── */
  .llc-state-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
    padding: 32px 24px;
  }
  .llc-state-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .llc-state-icon-loading { background: var(--navy-bg); }
  .llc-state-icon-error   { background: var(--red-bg); }

  .llc-state-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    text-align: center;
  }
  .llc-state-sub {
    font-size: 13px;
    color: var(--muted);
    text-align: center;
    max-width: 260px;
    line-height: 1.6;
    margin-top: -8px;
  }

  .llc-progress-track {
    width: 180px;
    height: 3px;
    background: var(--border);
    border-radius: 99px;
    overflow: hidden;
  }
  .llc-progress-bar {
    height: 100%;
    background: var(--navy);
    border-radius: 99px;
    animation: llc-progress 1.8s ease-in-out infinite;
  }
  @keyframes llc-progress {
    0%   { width: 0%;  margin-left: 0%; }
    50%  { width: 60%; margin-left: 20%; }
    100% { width: 0%;  margin-left: 100%; }
  }

  .llc-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 20px;
    background: var(--navy);
    color: #fff;
    border: none;
    border-radius: var(--radius-sm);
    font-size: 13px;
    font-weight: 600;
    font-family: var(--font);
    cursor: pointer;
    transition: background 0.15s;
  }
  .llc-retry-btn:hover { background: var(--navy-hover); }

  .rpv-toolbar { min-height: 40px !important; }
  .rpv-core__viewer { border-radius: 0 0 var(--radius) var(--radius); }
`;

function injectStyles() {
  if (document.getElementById("llc-pdf-style")) return;
  const el = document.createElement("style");
  el.id = "llc-pdf-style";
  el.textContent = STYLE;
  document.head.appendChild(el);
}

const LoadingScreen = () => (
  <div className="llc-state-screen">
    <div className="llc-state-icon llc-state-icon-loading">
      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 22, color: "var(--navy)" }} />
    </div>
    <p className="llc-state-title">Preparing your invoice</p>
    <p className="llc-state-sub">Generating the PDF document, this will only take a moment.</p>
    <div className="llc-progress-track">
      <div className="llc-progress-bar" />
    </div>
  </div>
);

const ErrorScreen = ({ message, onRetry }) => (
  <div className="llc-state-screen">
    <div className="llc-state-icon llc-state-icon-error">
      <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 22, color: "var(--red)" }} />
    </div>
    <p className="llc-state-title">Could not load document</p>
    <p className="llc-state-sub">{message || "Something went wrong while fetching the PDF. Please try again."}</p>
    <button className="llc-retry-btn" onClick={onRetry}>
      <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 12 }} />
      Try again
    </button>
  </div>
);

const PdfViewerPage = () => {
  const navigate     = useNavigate();
  const location     = useLocation();
  const myQueryParam = new URLSearchParams(location.search).get("oid");

  const [pdfUrl,  setPdfUrl]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  injectStyles();

  const base64ToBlob = (base64, contentType) => {
    const chars = atob(base64);
    const bytes = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    return new Blob([bytes], { type: contentType });
  };

  const loadPdf = useCallback(async () => {
    if (!myQueryParam) {
      setError("No document ID found in the URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response     = await fetchPdfData(myQueryParam);
      const base64String = response.data.responseObject;
      const blob         = base64ToBlob(base64String, "application/pdf");
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("PDF load error:", err);
      setError(err?.message || "Unable to fetch the document.");
    } finally {
      setLoading(false);
    }
  }, [myQueryParam]);

  useEffect(() => {
    loadPdf();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [loadPdf]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a    = document.createElement("a");
    a.href     = pdfUrl;
    a.download = `LLC-Invoice-${myQueryParam || "doc"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Native share sheet on mobile; fallback to download on desktop
  const handleShare = async () => {
    if (!pdfUrl) return;
    if (navigator.share) {
      try {
        const blob = await fetch(pdfUrl).then(r => r.blob());
        const file = new File([blob], `LLC-Invoice-${myQueryParam || "doc"}.pdf`, { type: "application/pdf" });
        await navigator.share({ files: [file], title: "Life Lab Care Invoice" });
      } catch (err) {
        if (err.name !== "AbortError") handleDownload();
      }
    } else {
      handleDownload();
    }
  };

  return (
    <div className="llc-root">

      <header className="llc-header">
        {/* Left: back icon + brand stack */}
        <div className="llc-header-left">
          <button
            className="llc-back-btn"
            onClick={() => navigate(`/invoice?oid=${myQueryParam}`)}
            aria-label="Back to payment"
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 13 }} />
          </button>
          <div className="llc-header-titles">
            <span className="llc-header-brand">Life Lab Care</span>
            <span className="llc-header-sub">
              {loading ? "Loading…" : `Tax Invoice · #${myQueryParam || "—"}`}
            </span>
          </div>
        </div>

        {/* Right: share + download */}
        <div className="llc-actions">
          <button
            className="llc-icon-btn"
            onClick={handleShare}
            disabled={!pdfUrl || loading}
            aria-label="Share invoice"
          >
            <FontAwesomeIcon icon={faShareNodes} style={{ fontSize: 13 }} />
          </button>

          <button
            className="llc-dl-btn"
            onClick={handleDownload}
            disabled={!pdfUrl || loading}
          >
            <FontAwesomeIcon icon={faDownload} style={{ fontSize: 11 }} />
            <span className="llc-dl-label">Download</span>
          </button>
        </div>
      </header>

      <main className="llc-body">
        <div className="llc-viewer-card">
          {loading && <LoadingScreen />}

          {!loading && error && (
            <ErrorScreen message={error} onRetry={loadPdf} />
          )}

          {!loading && !error && pdfUrl && (
            <div className="llc-viewer-inner">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </Worker>
            </div>
          )}
        </div>
      </main>

    </div>
  );
};

export default PdfViewerPage;